/**
 * chatCheckStore — Unified floating chat state for the Rater platform.
 * Manages messages, streaming, visibility, drag position, thread scope, etc.
 */
import { create } from 'zustand';
import { getStoredToken } from '../utils/authToken';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UIAction {
  action: string;
  target?: string;
  plant_id?: string;
  message?: string;
  level?: string;
}

export interface ChatMsg {
  id: string;
  role: 'user' | 'assistant' | 'action';
  content: string;
  timestamp: number;
  files?: string[];
  actionType?: string;
  actionMeta?: Record<string, unknown>;
}

export interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  description: string;
}

export interface PageContext {
  type: 'plant_detail' | 'plant_list' | 'rating' | 'marketplace' | 'admin' | 'portfolio';
  plantId?: string;
  plantName?: string;
  plantStatus?: string;
  plantRating?: string;
  action?: string;
}

export interface ChatConversation {
  id: string;
  plant_id?: string | null;
  feature?: 'rating' | 'upload' | 'improve' | 'general';
  title?: string;
  summary?: string;
  status?: string;
  created_at?: number;
  updated_at?: number;
}

export interface ChatCheckState {
  messages: ChatMsg[];
  conversations: ChatConversation[];
  isOpen: boolean;
  isMinimized: boolean;
  isStreaming: boolean;
  position: { x: number; y: number };
  selectedModel: string;
  availableModels: ModelInfo[];
  pageContext: PageContext | null;
  showThinking: boolean;
  activeConversationId: string | null;
  activeConversationPlantId: string | null;

  pendingActions: UIAction[];
  sendMessage: (message: string, plantId?: string, files?: File[]) => Promise<void>;
  loadConversations: (plantId?: string) => Promise<void>;
  switchConversation: (conversationId: string, plantId?: string) => Promise<void>;
  ensureConversation: (plantId?: string) => Promise<string | null>;
  startNewConversation: (plantId?: string) => Promise<string | null>;
  dispatchAction: (action: UIAction) => void;
  clearActions: () => void;
  setPageContext: (ctx: PageContext | null) => void;
  setShowThinking: (v: boolean) => void;
  trackAction: (actionType: string, content: string, plantId?: string, meta?: Record<string, unknown>) => void;
  loadHistory: (conversationId?: string) => Promise<void>;
  toggleOpen: () => void;
  toggleMinimize: () => void;
  setPosition: (x: number, y: number) => void;
  clearMessages: () => void;
  abortStreaming: () => void;
  setOpen: (open: boolean) => void;
  setMinimized: (min: boolean) => void;
  setSelectedModel: (model: string) => void;
  loadAvailableModels: () => Promise<void>;
}

// ─── ID helper ──────────────────────────────────────────────────────────────

let _seq = 0;
function nextId(): string {
  return `uchat_${Date.now()}_${++_seq}`;
}

// ─── Abort controller ───────────────────────────────────────────────────────

let _abortCtrl: AbortController | null = null;

// ─── Fetch helpers ──────────────────────────────────────────────────────────

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = getStoredToken();
  return {
    ...(extra ?? {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function normalizePlantScope(plantId?: string | null): string | null {
  const value = (plantId ?? '').trim();
  return value || null;
}

function isConversationNotFoundError(status: number, payload: string): boolean {
  return status === 404 && payload.toLowerCase().includes('conversation not found');
}

function inferFeatureFromContext(ctx?: PageContext | null): 'rating' | 'upload' | 'improve' | 'general' {
  const action = (ctx?.action ?? '').toLowerCase();
  if (action.includes('upload')) return 'upload';
  if (action.includes('improve')) return 'improve';

  switch (ctx?.type) {
    case 'rating':
      return 'rating';
    case 'plant_detail':
      return action.includes('analy') ? 'improve' : 'general';
    default:
      return 'general';
  }
}

async function fetchCurrentConversation(plantId?: string | null): Promise<ChatConversation | null> {
  const scope = normalizePlantScope(plantId);
  const query = scope ? `?plant_id=${encodeURIComponent(scope)}` : '';
  const resp = await fetch(`/api/v2/chat/conversations/current${query}`, {
    headers: authHeaders(),
  });
  if (!resp.ok) {
    throw new Error(`Failed to load conversation: ${resp.status}`);
  }
  const data = await resp.json();
  return (data.data ?? null) as ChatConversation | null;
}

async function fetchConversations(plantId?: string | null): Promise<ChatConversation[]> {
  const scope = normalizePlantScope(plantId);
  const query = scope ? `?plant_id=${encodeURIComponent(scope)}` : '';
  const resp = await fetch(`/api/v2/chat/conversations${query}`, {
    headers: authHeaders(),
  });
  if (!resp.ok) {
    throw new Error(`Failed to load conversations: ${resp.status}`);
  }
  const data = await resp.json();
  return (data.data ?? []) as ChatConversation[];
}

async function createConversation(
  plantId?: string | null,
  archiveCurrentId?: string | null,
  feature: 'rating' | 'upload' | 'improve' | 'general' = 'general',
): Promise<ChatConversation> {
  const resp = await fetch('/api/v2/chat/conversations', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      feature,
      ...(normalizePlantScope(plantId) ? { plant_id: normalizePlantScope(plantId) } : {}),
      ...(archiveCurrentId ? { archive_current_id: archiveCurrentId } : {}),
    }),
  });
  if (!resp.ok) {
    const errText = await resp.text().catch(() => '');
    throw new Error(`Failed to create conversation: ${resp.status} ${errText}`);
  }
  const data = await resp.json();
  return data.data as ChatConversation;
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useChatCheckStore = create<ChatCheckState>()((set, get) => ({
  messages: [],
  conversations: [],
  isOpen: false,
  isMinimized: false,
  isStreaming: false,
  position: { x: -1, y: -1 },
  selectedModel: '',
  availableModels: [],
  pendingActions: [],
  pageContext: null,
  showThinking: false,
  activeConversationId: null,
  activeConversationPlantId: null,

  setShowThinking: (v) => set({ showThinking: v }),
  setPageContext: (ctx) => set({ pageContext: ctx }),
  dispatchAction: (action) => set((s) => ({ pendingActions: [...s.pendingActions, action] })),
  clearActions: () => set({ pendingActions: [] }),

  trackAction: (actionType, content, plantId, meta) => {
    fetch('/api/v2/chat/action', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ action_type: actionType, content, plant_id: plantId ?? '', meta: meta ?? {} }),
    }).catch(() => {});
  },

  loadHistory: async (conversationId?: string) => {
    const token = getStoredToken();
    if (!token || !conversationId) {
      set({ messages: [] });
      return;
    }
    try {
      const resp = await fetch(`/api/v2/chat/history?conversation_id=${encodeURIComponent(conversationId)}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        set({ messages: [] });
        return;
      }
      const data = await resp.json();
      const history: ChatMsg[] = (data.data ?? [])
        .filter((m: any) => m.role !== 'action')
        .map((m: any) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant' | 'action',
          content: m.content,
          timestamp: m.timestamp,
          actionType: m.action_type ?? undefined,
          actionMeta: m.action_meta ?? undefined,
        }));
      set({ messages: history });
    } catch {
      set({ messages: [] });
    }
  },

  loadConversations: async (plantId?: string) => {
    const token = getStoredToken();
    if (!token) {
      set({ conversations: [] });
      return;
    }
    try {
      const items = await fetchConversations(plantId ?? get().pageContext?.plantId);
      set({ conversations: items });
    } catch {
      set({ conversations: [] });
    }
  },

  switchConversation: async (conversationId: string, plantId?: string) => {
    const scopePlantId = normalizePlantScope(plantId ?? get().pageContext?.plantId);
    set({
      activeConversationId: conversationId,
      activeConversationPlantId: scopePlantId,
      messages: [],
    });
    await get().loadHistory(conversationId);
  },

  ensureConversation: async (plantId?: string) => {
    const scopePlantId = normalizePlantScope(plantId ?? get().pageContext?.plantId);
    if (get().activeConversationId && get().activeConversationPlantId === scopePlantId) {
      return get().activeConversationId;
    }

    let conversation = await fetchCurrentConversation(scopePlantId);
    if (!conversation) {
      conversation = await createConversation(scopePlantId, null, inferFeatureFromContext(get().pageContext));
    }

    set({
      activeConversationId: conversation.id,
      activeConversationPlantId: normalizePlantScope(conversation.plant_id),
      messages: [],
    });
    await get().loadHistory(conversation.id);
    await get().loadConversations(scopePlantId ?? undefined);
    return conversation.id;
  },

  startNewConversation: async (plantId?: string) => {
    const scopePlantId = normalizePlantScope(plantId ?? get().pageContext?.plantId);
    const feature = inferFeatureFromContext(get().pageContext);
    let conversation: ChatConversation;
    try {
      conversation = await createConversation(scopePlantId, get().activeConversationId, feature);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (!isConversationNotFoundError(404, message)) {
        throw error;
      }
      conversation = await createConversation(scopePlantId, null, feature);
    }
    set({
      activeConversationId: conversation.id,
      activeConversationPlantId: normalizePlantScope(conversation.plant_id),
      messages: [],
    });
    await get().loadConversations(scopePlantId ?? undefined);
    return conversation.id;
  },

  setSelectedModel: (model) => set({ selectedModel: model }),

  loadAvailableModels: async () => {
    try {
      const resp = await fetch('/api/v2/chat/models', { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        const models = data.data ?? data ?? [];
        if (!Array.isArray(models) || models.length === 0) return;
        set({ availableModels: models });
        // Only set default model if none selected or current selection no longer valid
        const selectedModel = get().selectedModel;
        if (!selectedModel || !models.some((m: ModelInfo) => m.id === selectedModel)) {
          set({ selectedModel: models[0].id });
        }
      }
    } catch (e) {
      console.warn('[chatCheckStore] Failed to load models:', e);
    }
  },

  toggleOpen: () => set((s) => ({ isOpen: !s.isOpen })),
  setOpen: (open) => set({ isOpen: open }),
  toggleMinimize: () => set((s) => ({ isMinimized: !s.isMinimized })),
  setMinimized: (min) => set({ isMinimized: min }),
  setPosition: (x, y) => set({ position: { x, y } }),
  clearMessages: () => set({ messages: [] }),

  abortStreaming: () => {
    if (_abortCtrl) {
      _abortCtrl.abort();
      _abortCtrl = null;
    }
    set({ isStreaming: false });
  },

  sendMessage: async (message: string, plantId?: string, files?: File[]) => {
    const trimmed = message.trim();
    if (!trimmed || get().isStreaming) return;

    const effectivePlantId = normalizePlantScope(plantId ?? get().pageContext?.plantId);
    let conversationId = get().activeConversationId;
    if (!conversationId || get().activeConversationPlantId !== effectivePlantId) {
      conversationId = await get().ensureConversation(effectivePlantId ?? undefined);
    }
    if (!conversationId) {
      throw new Error('No active conversation available');
    }

    const fileNames = files?.map((f) => f.name) ?? [];
    const userMsg: ChatMsg = {
      id: nextId(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
      files: fileNames.length > 0 ? fileNames : undefined,
    };

    const assistantId = nextId();
    const placeholder: ChatMsg = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg, placeholder],
      isStreaming: true,
    }));

    if (_abortCtrl) _abortCtrl.abort();
    _abortCtrl = new AbortController();
    const requestAbortCtrl = _abortCtrl;

    let accumulated = '';

    try {
      const ctx = get().pageContext;
      // Enrich page context with marketplace action state
      let enrichedCtx: Record<string, unknown> | null = ctx ? { ...ctx } : null;
      try {
        const { useMarketplaceActionStore } = await import('./marketplaceActionStore');
        const mktState = useMarketplaceActionStore.getState();
        if (mktState.pageListingId) {
          enrichedCtx = enrichedCtx ?? {};
          enrichedCtx.listing_id = mktState.pageListingId;
          enrichedCtx.listing_status = mktState.pageListingStatus;
          enrichedCtx.buy_interest_submitted = mktState.submittedInterestIds.has(mktState.pageListingId);
        }
        if (mktState.showFavoritesOnly) {
          enrichedCtx = enrichedCtx ?? {};
          enrichedCtx.showing_favorites_only = true;
        }
        if (mktState.submittedInterestIds.size > 0) {
          enrichedCtx = enrichedCtx ?? {};
          enrichedCtx.submitted_interest_count = mktState.submittedInterestIds.size;
        }
        if (mktState.selectedCompareIds.size > 0) {
          enrichedCtx = enrichedCtx ?? {};
          enrichedCtx.selected_compare_ids = [...mktState.selectedCompareIds];
        }
      } catch { /* marketplaceActionStore not available on all pages */ }

      const buildRequest = (targetConversationId: string) => {
        let body: BodyInit;
        const headers: Record<string, string> = {};

        if (files && files.length > 0) {
          const fd = new FormData();
          fd.append('message', trimmed);
          fd.append('conversation_id', targetConversationId);
          if (effectivePlantId) fd.append('plant_id', effectivePlantId);
          if (enrichedCtx) fd.append('page_context', JSON.stringify(enrichedCtx));
          if (get().selectedModel) fd.append('model', get().selectedModel);
          if (get().showThinking) fd.append('show_thinking', 'true');
          files.forEach((f) => fd.append('files', f));
          body = fd;
        } else {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify({
            message: trimmed,
            conversation_id: targetConversationId,
            ...(effectivePlantId ? { plant_id: effectivePlantId } : {}),
            ...(enrichedCtx ? { page_context: enrichedCtx } : {}),
            ...(get().selectedModel ? { model: get().selectedModel } : {}),
            ...(get().showThinking ? { show_thinking: true } : {}),
          });
        }

        return { body, headers };
      };

      const postChat = async (targetConversationId: string) => {
        const { body, headers } = buildRequest(targetConversationId);
        return fetch('/api/v2/chat', {
          method: 'POST',
          headers: authHeaders(headers),
          body,
          signal: requestAbortCtrl.signal,
        });
      };

      let response = await postChat(conversationId);
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        if (isConversationNotFoundError(response.status, errText)) {
          const recoveredConversation = await createConversation(effectivePlantId, null, inferFeatureFromContext(get().pageContext));
          conversationId = recoveredConversation.id;
          set((s) => ({
            activeConversationId: recoveredConversation.id,
            activeConversationPlantId: normalizePlantScope(recoveredConversation.plant_id),
            conversations: [recoveredConversation, ...s.conversations.filter((item) => item.id !== recoveredConversation.id)],
          }));
          response = await postChat(conversationId);
        }
      }

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`Server error: ${response.status} ${errText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'thinking' && parsed.reasoning) {
              set((s) => {
                const last = s.messages[s.messages.length - 1];
                if (last && last.role === 'action' && last.actionType === 'thinking') {
                  return {
                    messages: s.messages.map((m) =>
                      m.id === last.id ? { ...m, content: m.content + parsed.reasoning } : m,
                    ),
                  };
                }
                return {
                  messages: [...s.messages, {
                    id: nextId(),
                    role: 'action' as const,
                    content: parsed.reasoning,
                    timestamp: Date.now(),
                    actionType: 'thinking',
                  }],
                };
              });
              continue;
            }

            if (parsed.type === 'action') {
              set((s) => ({ pendingActions: [...s.pendingActions, parsed as UIAction] }));
              continue;
            }

            if (parsed.type === 'tool_call' || parsed.type === 'tool_progress' || parsed.type === 'step_update' || parsed.type === 'step_start' || parsed.type === 'step_complete') {
              continue;
            }

            if (parsed.type === 'error') {
              const userMessage =
                typeof parsed.message === 'string' && parsed.message
                  ? parsed.message
                  : 'The AI model connection was interrupted. Please try again.';
              // If we already have partial content, keep it and append error note
              if (accumulated) {
                accumulated += `\n\n*${userMessage}*`;
                set((s) => ({
                  messages: s.messages.map((m) =>
                    m.id === assistantId ? { ...m, content: accumulated } : m,
                  ),
                }));
              } else {
                accumulated = userMessage;
                set((s) => ({
                  messages: s.messages.map((m) =>
                    m.id === assistantId ? { ...m, content: accumulated } : m,
                  ),
                }));
              }
              continue;
            }

            const delta = parsed.delta ?? parsed.content ?? parsed.text ?? '';
            if (typeof delta === 'string' && delta) {
              accumulated += delta;
              set((s) => ({
                messages: s.messages.map((m) =>
                  m.id === assistantId ? { ...m, content: accumulated } : m,
                ),
              }));
            }
          } catch {
            accumulated += data;
            set((s) => ({
              messages: s.messages.map((m) =>
                m.id === assistantId ? { ...m, content: accumulated } : m,
              ),
            }));
          }
        }
      }

      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === assistantId
            ? { ...m, content: accumulated || 'No response received.' }
            : m,
        ),
        isStreaming: false,
      }));
      await get().loadConversations(effectivePlantId ?? undefined);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === assistantId
              ? { ...m, content: accumulated || 'Stream cancelled.' }
              : m,
          ),
          isStreaming: false,
        }));
        return;
      }
      const errMsg = err instanceof Error ? err.message : 'Connection error';
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === assistantId
            ? { ...m, content: `Error: ${errMsg}` }
            : m,
        ),
        isStreaming: false,
      }));
    } finally {
      _abortCtrl = null;
    }
  },
}));
